import { ApiError } from "@/lib/api-client";

// Maps a failed seat-request POST to a Banner descriptor (Story 14.7, gh-171).
// Deterministic 4xx account/data states get calm, dedicated branches instead
// of a generic retry (conventions lesson: retry must never be the only answer
// to a state that is a property of the account). ORG-004 means a request WAS
// already admitted this rate-limit window (rejected attempts are not recorded
// by the backend limiter), so the copy reassures rather than alarms; ORG-002
// mirrors the dashboard's no-professional-org posture. Anything else is a
// transport-grade failure — the CTA itself stays enabled as the retry
// affordance, so the danger banner carries no dead button.
//
// [Source: src/application/organizations/_shared/seat_request_rate_limiter.py
//  (429 ORG-004); src/application/organizations/_shared/exceptions.py
//  (409 ORG-002 no_professional_organization)]

export interface SeatRequestBanner {
  variant: "info" | "danger";
  title: string;
  body: string;
}

const GENERIC: SeatRequestBanner = {
  variant: "danger",
  title: "Couldn't send your seat request",
  body: "Please try again.",
};

const BY_CODE: Record<string, SeatRequestBanner> = {
  "ORG-004": {
    variant: "info",
    title: "You've recently requested seats",
    // "15 minutes" mirrors the limiter's real WINDOW_SECONDS = 900 (review
    // finding: "a few minutes" understated the retry horizon).
    body: "A request was already sent to the AMPS Director within the last 15 minutes. They will contact you to arrange additional seats.",
  },
  "ORG-002": {
    variant: "info",
    // Distinct title — the dashboard already renders its own "No organization
    // yet" banner in this state; a duplicate title would stack two identical
    // announcements (review finding).
    title: "Seat request not sent",
    body: "Your account isn't linked to an organization seat pool yet, so the request wasn't sent. CITS sets up organizations directly.",
  },
};

export function toSeatRequestBanner(error: unknown): SeatRequestBanner {
  // Own-property guard: `code` is a server-supplied string, so a value that
  // collides with an Object.prototype member (`toString`, `constructor`,
  // `__proto__`) would resolve through the prototype chain to a truthy non-Banner
  // and slip past `?? GENERIC`. The own-property check restricts the lookup to
  // real entries — anything else falls back to the generic danger banner.
  if (
    error instanceof ApiError &&
    Object.prototype.hasOwnProperty.call(BY_CODE, error.code)
  ) {
    return BY_CODE[error.code];
  }
  return GENERIC;
}
